import { Injectable } from '@angular/core';
import { BehaviorSubject, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { CreditsManagementService } from './credits-management.service';
import { TotalCredits } from '../interfaces/credits-management.interface';

export const CREDIT_WARNING_THRESHOLD_PERCENT = 95;
// Mirrors generic-bot's CreditsBackendAPI.CREDIT_PER_WHATSAPP_MESSAGE: it blocks
// (and silently drops) a send when the balance can't cover one message, so a
// fractional balance like 0.92 must already count as exhausted here.
export const CREDITS_PER_WHATSAPP_MESSAGE = 1;

export interface CreditsStatus {
  companyId: string | null;
  percentUsed: number | null;
  isExhausted: boolean;
  isNearLimit: boolean;
  // Both keyed to `companyId` (not a separate flag) so a consumer only has to
  // check one field to know which company they describe — see
  // ChatInputVisibilityService.isCreditsStatusPending.
  isLoading: boolean;
  hasError: boolean;
  /** Raw `/company/credits` response; only present once it resolved for `companyId`. */
  credits?: TotalCredits;
}

const INITIAL_STATUS: CreditsStatus = {
  companyId: null,
  percentUsed: null,
  isExhausted: false,
  isNearLimit: false,
  isLoading: false,
  hasError: false,
};

@Injectable({ providedIn: 'root' })
export class CreditsStatusService {
  private readonly statusSubject = new BehaviorSubject<CreditsStatus>(INITIAL_STATUS);
  readonly status$ = this.statusSubject.asObservable();

  constructor(private readonly creditsManagementService: CreditsManagementService) {}

  get status(): CreditsStatus {
    return this.statusSubject.value;
  }

  refresh(companyId: string, channelReferenceId?: string) {
    // Set synchronously (not inside the pipe) so it's visible immediately to
    // ChatInputVisibilityService.determineTextInputState$, which runs right
    // after this is called — before the HTTP request has a chance to resolve.
    this.statusSubject.next({ ...INITIAL_STATUS, companyId, isLoading: true });

    return this.creditsManagementService.getCompanyCredits(companyId, channelReferenceId).pipe(
      tap((totalCredits) => {
        const percentUsed =
          totalCredits.total > 0
            ? Math.round(
                ((totalCredits.total - totalCredits.totalRemaining) / totalCredits.total) * 100
              )
            : 0;
        const isExhausted = totalCredits.totalRemaining < CREDITS_PER_WHATSAPP_MESSAGE;

        this.statusSubject.next({
          companyId,
          percentUsed,
          isExhausted,
          isNearLimit: !isExhausted && percentUsed >= CREDIT_WARNING_THRESHOLD_PERCENT,
          isLoading: false,
          hasError: false,
          credits: totalCredits,
        });
      }),
      catchError((error) => {
        this.statusSubject.next({ ...INITIAL_STATUS, companyId, hasError: true });
        return throwError(() => error);
      })
    );
  }

  reset() {
    this.statusSubject.next(INITIAL_STATUS);
  }
}
